import { Badge, Box, Flex, Input, Text, VStack } from "@chakra-ui/react"
import { useQuery } from "@tanstack/react-query"
import { useState } from "react"
import { FaTimes } from "react-icons/fa"

import { QuizzesService } from "@/client"

interface TagSelectorProps {
  selectedTags: string[]
  onChange: (tags: string[]) => void
}

export function TagSelector({ selectedTags, onChange }: TagSelectorProps) {
  const [searchQuery, setSearchQuery] = useState("")
  const [showSuggestions, setShowSuggestions] = useState(false)
  const { data: tags = [] } = useQuery({
    queryKey: ["quiz-tags"],
    queryFn: () => QuizzesService.listQuizTags(),
  })
  const filteredTags = tags.filter(
    (tag) =>
      !selectedTags.includes(tag) &&
      (!searchQuery.trim() ||
        tag.toLowerCase().includes(searchQuery.toLowerCase())),
  )

  const addTag = (tag: string) => {
    onChange([...selectedTags, tag])
    setSearchQuery("")
    setShowSuggestions(false)
  }

  return (
    <Box width="100%">
      <Box position="relative">
        <Input
          placeholder="Search or click to view all tags..."
          value={searchQuery}
          onChange={(event) => {
            setSearchQuery(event.target.value)
            setShowSuggestions(true)
          }}
          onFocus={() => setShowSuggestions(true)}
          onKeyDown={(event) => {
            if (event.key === "Enter" && filteredTags.length > 0) {
              event.preventDefault()
              addTag(filteredTags[0])
            }
          }}
        />
        {showSuggestions && (
          <Box
            position="absolute"
            top="100%"
            left={0}
            right={0}
            mt={1}
            bg="bg.panel"
            borderWidth="1px"
            borderRadius="md"
            boxShadow="lg"
            maxH="300px"
            overflowY="auto"
            zIndex={10}
          >
            <VStack align="stretch" gap={0}>
              {filteredTags.length > 0 ? (
                filteredTags.map((tag) => (
                  <Box
                    key={tag}
                    px={3}
                    py={2}
                    cursor="pointer"
                    _hover={{ bg: "bg.muted" }}
                    onClick={() => addTag(tag)}
                  >
                    <Text>{tag}</Text>
                  </Box>
                ))
              ) : (
                <Box px={3} py={2}>
                  <Text color="fg.muted">No tags available.</Text>
                </Box>
              )}
            </VStack>
          </Box>
        )}
      </Box>
      {showSuggestions && (
        <Box
          position="fixed"
          inset={0}
          zIndex={9}
          onClick={() => setShowSuggestions(false)}
        />
      )}
      {selectedTags.length > 0 && (
        <Flex gap={2} wrap="wrap" mt={3}>
          {selectedTags.map((tag) => (
            <Badge
              key={tag}
              colorPalette="blue"
              size="lg"
              py={1}
              px={2}
              display="flex"
              alignItems="center"
              gap={2}
            >
              {tag}
              <button
                type="button"
                aria-label={`Remove ${tag}`}
                onClick={() =>
                  onChange(selectedTags.filter((item) => item !== tag))
                }
              >
                <FaTimes />
              </button>
            </Badge>
          ))}
        </Flex>
      )}
    </Box>
  )
}
